import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Brain,
  Calendar,
  FileText,
  Files,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Utensils,
} from 'lucide-react';
import {
  APP_NAME,
  APP_TAGLINE,
  DEFAULT_PAGE_DESCRIPTION,
} from '../config/appConfig';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
}

interface ValueProp {
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: <FileText size={40} />,
    title: 'Imaging Review',
    description:
      'Upload X-rays or PDFs for AI-assisted diagnostic summaries with clear next-step recommendations.',
    link: '/xray-diagnosis',
  },
  {
    icon: <Utensils size={40} />,
    title: 'Wellness Plans',
    description:
      'Generate personalized nutrition and sleep guidance based on each patient profile.',
    link: '/health-plans',
  },
  {
    icon: <Calendar size={40} />,
    title: 'Care Scheduling',
    description:
      'Streamline appointment intake with patient details, visit context, and provider preferences.',
    link: '/appointments',
  },
  {
    icon: <Files size={40} />,
    title: 'Document QA',
    description:
      'Upload healthcare documents, retrieve relevant chunks locally, and get grounded Groq answers with citations.',
    link: '/document-qa',
  },
  {
    icon: <Brain size={40} />,
    title: 'Mental Wellness Support',
    description:
      'Offer always-available, empathetic support conversations that complement clinical care pathways.',
    link: '/mental-health',
  },
];

const valueProps: ValueProp[] = [
  {
    title: 'Built For Real Care Journeys',
    description:
      'The experience connects diagnostics, wellness planning, scheduling, and support in one flow instead of scattering them across separate tools.',
  },
  {
    title: 'Designed For Clarity',
    description:
      'Each feature presents concise output, approachable language, and polished interfaces that work well in demos and interviews.',
  },
  {
    title: 'Ready To Extend',
    description:
      'The codebase keeps API and UI concerns separated so future integrations like auth, persistence, or analytics can be added cleanly.',
  },
];

const quickLinks = [
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
  { label: 'Privacy', href: '/privacy' },
];

const HomePage: React.FC = () => {
  useDocumentMetadata('Intelligent Healthcare Workspace', DEFAULT_PAGE_DESCRIPTION);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-sky-100 bg-white/95 backdrop-blur">
        <div className="container mx-auto flex flex-col gap-4 px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-sky-100 p-2 text-sky-700">
                <Activity size={20} />
              </div>
              <div>
                <p className="text-xl font-bold tracking-tight">{APP_NAME}</p>
                <p className="text-sm text-slate-500">AI-enabled care experience</p>
              </div>
            </div>
          </div>
          <nav>
            <ul className="flex flex-wrap gap-5 text-sm font-medium text-slate-600">
              <li><Link to="/" className="transition hover:text-sky-700">Home</Link></li>
              <li><Link to="/about" className="transition hover:text-sky-700">About</Link></li>
              <li><Link to="/services" className="transition hover:text-sky-700">Services</Link></li>
              <li><Link to="/contact" className="transition hover:text-sky-700">Contact</Link></li>
            </ul>
          </nav>
        </div>
      </header>

      <main>
        <section className="overflow-hidden bg-gradient-to-br from-sky-700 via-sky-600 to-cyan-500 text-white">
          <div className="container mx-auto grid gap-12 px-6 py-20 md:grid-cols-[1.2fr_0.8fr] md:items-center">
            <div>
              <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-medium">
                <Sparkles size={16} />
                Interview-ready full-stack healthcare platform
              </p>
              <h1 className="max-w-3xl text-4xl font-bold leading-tight md:text-5xl">
                {APP_NAME} brings diagnostics, planning, and patient support into one polished workspace.
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-sky-50">
                {APP_TAGLINE}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/services"
                  className="rounded-full bg-white px-6 py-3 font-semibold text-sky-700 transition hover:bg-sky-50"
                >
                  Explore Services
                </Link>
                <Link
                  to="/appointments"
                  className="rounded-full border border-white/50 px-6 py-3 font-semibold text-white transition hover:bg-white/10"
                >
                  Book a Demo Appointment
                </Link>
              </div>
            </div>
            <div className="grid gap-4">
              <div className="rounded-3xl bg-white/10 p-6 backdrop-blur">
                <div className="mb-4 flex items-center gap-3">
                  <ShieldCheck className="text-cyan-100" />
                  <p className="text-lg font-semibold">Clinical workflow coverage</p>
                </div>
                <p className="text-sm leading-6 text-sky-50">
                  From imaging uploads to care-plan generation, the product demonstrates how AI can support both patient-facing and operational healthcare journeys.
                </p>
              </div>
              <div className="rounded-3xl bg-slate-950/20 p-6 backdrop-blur">
                <div className="mb-4 flex items-center gap-3">
                  <Stethoscope className="text-cyan-100" />
                  <p className="text-lg font-semibold">Clear product storytelling</p>
                </div>
                <p className="text-sm leading-6 text-sky-50">
                  The project is structured for demos, portfolio walkthroughs, and technical interviews where readability and maintainability matter.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-slate-900">Core Product Capabilities</h2>
              <p className="mt-3 text-slate-600">
                Five focused experiences demonstrate how the platform supports patients and care teams.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {features.map((feature) => (
                <FeatureCard key={feature.title} {...feature} />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="container mx-auto px-6">
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-slate-900">Why Teams Choose {APP_NAME}</h2>
              <p className="mt-3 text-slate-600">
                The project keeps the original feature set while presenting it with stronger engineering and product polish.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {valueProps.map((valueProp) => (
                <ValueCard key={valueProp.title} {...valueProp} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="rounded-3xl bg-slate-900 px-8 py-12 text-center text-white">
              <h2 className="text-3xl font-bold">Ready to explore the platform?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-slate-300">
                Review the service catalog, inspect the feature flows, and walk through the product like a real stakeholder demo.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/xray-diagnosis"
                  className="rounded-full bg-cyan-400 px-6 py-3 font-semibold text-slate-900 transition hover:bg-cyan-300"
                >
                  Analyze Imaging
                </Link>
                <Link
                  to="/mental-health"
                  className="rounded-full border border-slate-600 px-6 py-3 font-semibold text-white transition hover:bg-slate-800"
                >
                  Open Wellness Support
                </Link>
                <Link
                  to="/document-qa"
                  className="rounded-full border border-slate-600 px-6 py-3 font-semibold text-white transition hover:bg-slate-800"
                >
                  Open Document QA
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="container mx-auto flex flex-col gap-6 px-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{APP_NAME}</h3>
            <p className="mt-2 max-w-xl text-sm text-slate-600">{APP_TAGLINE}</p>
            <p className="mt-3 text-sm text-slate-500">Created by Ayan113</p>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            {quickLinks.map((link) => (
              <Link key={link.href} to={link.href} className="transition hover:text-sky-700">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard: React.FC<Feature> = ({ icon, title, description, link }) => (
  <Link
    to={link}
    className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:border-sky-200 hover:shadow-lg"
  >
    <div className="flex flex-col items-start">
      <div className="mb-5 rounded-2xl bg-sky-50 p-4 text-sky-700">{icon}</div>
      <h3 className="text-2xl font-semibold text-slate-900">{title}</h3>
      <p className="mt-3 text-slate-600">{description}</p>
    </div>
  </Link>
);

const ValueCard: React.FC<ValueProp> = ({ title, description }) => (
  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8">
    <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
    <p className="mt-3 text-slate-600">{description}</p>
  </div>
);

export default HomePage;
